import { LifeBuoy } from "lucide-react";
import { useReportIssue } from "@/components/issues/ReportIssueProvider";
import { ReleaseSummaryDialog } from "@/components/layout/ReleaseSummaryDialog";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { Button } from "@/components/ui/button";
import { useAppInfo } from "@/hooks/use-app-info";
import { CAPTURE_IGNORE_PROPS } from "@/lib/screenshot-capture";
import { cn } from "@/lib/utils";

/**
 * The slim bottom bar on every signed-in page: app version (click it for that release's
 * summary), copyright, the notifications
 * bell (filled with the unread count while anything is new), and the
 * life ring in the bottom-right corner that opens and closes the Report an Issue panel
 * (src/components/issues/). A dot on the ring means a report is waiting in the closed
 * panel. The ring is left out of issue screenshots.
 */
function VersionLabel() {
  const { data, isLoading, isError } = useAppInfo();
  if (isLoading) return null;
  if (isError || !data?.version) return <span>Version unavailable</span>;
  return <ReleaseSummaryDialog appInfo={{ ...data, version: data.version }} />;
}

export function StatusBar() {
  const year = new Date().getFullYear();
  const { open, inProgress, toggleReportIssue } = useReportIssue();

  return (
    <footer className="flex h-7 shrink-0 items-center gap-3 border-t border-border bg-background pr-1 pl-3 text-xs text-muted-foreground">
      <span className="flex shrink-0 items-center gap-1.5 tabular-nums">
        <span className="font-medium text-primary">Nymbl Staff</span>
        <VersionLabel />
      </span>
      <span className="min-w-0 flex-1 truncate">© {year} Nymbl LLC. All rights reserved.</span>
      <NotificationBell />
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={toggleReportIssue}
        aria-expanded={open}
        aria-label={
          inProgress && !open ? "Report an issue — you have one in progress" : "Report an issue"
        }
        title={inProgress && !open ? "Report an issue (in progress)" : "Report an issue"}
        {...CAPTURE_IGNORE_PROPS}
        className={cn(
          "relative text-primary hover:bg-primary/10 hover:text-primary",
          open && "bg-primary/10",
        )}
      >
        <LifeBuoy className="size-4" />
        {inProgress && !open && (
          <span
            aria-hidden
            className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-primary ring-1 ring-background"
          />
        )}
      </Button>
    </footer>
  );
}
